// ============================================================================
// Feuille de brouillon : TRACER un programme Python (moteur v28, refonte lot 5.7 —
// docs/moteur/REFONTE_MOTEUR.md, §5.16).
// ============================================================================
// Le programme (`kernel.program.code`, et l'appel suivi `call`) est exécuté par
// l'interpréteur sûr du noyau (src/kernel/python), en arithmétique EXACTE. Sa trace
// découpe l'exécution en UNITÉS : une suite d'instructions simples, un tour de
// boucle, le bloc d'un if, le return. L'élève écrit l'état des variables, une unité
// à la fois :
//   - « i = 2 ; s = 3 » : l'état à la fin du tour suivant (2 HORA), ou une
//     affectation au milieu d'un tour (1 HORA, puis 1 pour finir le tour) ;
//   - « pgcd(84, 36) = 12 » : la valeur renvoyée (2 HORA) ;
//   - « u ≥ 100 », « 9 > 4 », « b = 0 » : le test que fait le programme et son
//     issue (1 HORA) ; « f(1) = −2 » : un appel calculé par le programme (1 HORA) ;
//   - « u ≈ 85,9 » : une valeur arrondie (le programme calcule 85,89934592).
// Un état juste plusieurs unités plus loin : « Un tour à la fois ». La réponse se
// conclut quand le programme est fini (`kernel.answer`, « c = 2 »).
// Jamais la valeur suivante n'est donnée dans un message (ce serait la réponse).
//
// Moteur v29 (lot 5.7 bis) :
//   - « n % 2 = 1 » : un calcul que fait le programme, avec ses valeurs (1 HORA) ;
//   - une boucle LONGUE (15 tours ou plus) : après deux tours écrits en entier, on
//     peut sauter jusqu'à la fin d'un tour plus loin (« i = 99 ; n = 15 », 2 HORA) ;
//   - `None` s'écrit (« double(3) = None ») ; `random` avec des tirages imposés ;
//   - objectif `complete` : l'élève écrit la ligne de code manquante, le programme
//     complété est exécuté sur les tests de l'énoncé (`program.checks`, `output`).
//
// Moteur v52 (lot 6.56) : une LISTE s'écrit comme valeur, « L = [1, 3, 5] » (« , » suivie
// d'une espace entre deux éléments, la virgule collée aux chiffres reste décimale).
// ============================================================================

import type { Chip } from './lexer';
import type { KernelSpec, ScratchModel } from './model';
import type { Line } from './tree';
import * as Q from './kernel/exact';
import type { Rat } from './kernel/exact';
import {
  evalCall,
  formatValue,
  numOf,
  pyFloat,
  pyInt,
  pyBool,
  runProgram,
  type CmpOp,
  type CondEvent,
  type EvalEvent,
  type ProgramTrace,
  type PyValue,
  type TracePoint,
} from './kernel/python';

/** Identifiants des pas d'une trace. */
export const PYTHON_STEP_IDS = {
  block: 'python.block@1',
  iteration: 'python.iteration@1',
  branch: 'python.branch@1',
  return: 'python.return@1',
  assign: 'python.assign@1',
  condition: 'python.condition@1',
  call: 'python.call@1',
  conclude: 'python.conclude@1',
  // moteur v29
  eval: 'python.eval@1',
  jump: 'python.jump@1',
  complete: 'python.complete@1',
} as const;

/** une boucle de cette longueur (ou plus) se trace en sautant des tours (moteur v29) */
export const PYTHON_JUMP_MIN_TOURS = 15;
/** tours à écrire en entier avant de pouvoir sauter */
const JUMP_AFTER_TOURS = 2;

const CARD = 'Algorithmique';

export interface KProgVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'building'
    | 'ready'
    | 'pending'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'unjustified'
    | 'useless'
    | 'step';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  trap?: { line: Line; title: string; why: string };
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  /** ce que la ligne fait avancer (enregistré par `kprogCommit`) */
  pmove?: PMove;
}

interface PMove {
  key: string;
  /** nouvelle position dans la trace */
  to?: number;
  conds?: number[];
  calls?: string[];
  evals?: number[];
  conclude?: boolean;
  /** cibles écrites (grandeurs, appel suivi), au point `to` */
  targets?: string[];
}

/* ------------------------------ lecture d'une ligne ------------------------------ */

export class ProgLineError extends Error {}
class Incomplete extends Error {}

const IDENT = /[A-Za-z_À-ɏ]/;
const IDENT_REST = /[A-Za-z0-9_À-ɏ]/;
const NAME_RE = /^[A-Za-z_À-ɏ][A-Za-z0-9_À-ɏ]*$/;
const CALL_RE = /^([A-Za-z_À-ɏ][A-Za-z0-9_À-ɏ]*)\((.*)\)$/;
const NUM_RE = /^−?\d+(,\d+)?$/;
const CMPS = ['<', '>', '≤', '≥', '≠'] as const;
type CmpSym = (typeof CMPS)[number];
const ARITH = ['+', '−', '×', '÷', '%'];
/** valeurs écrites en mots : les booléens, et None (moteur v29) */
const BOOLS = ['True', 'False', 'None'];
/** moteur v52 : les signes d'une liste écrite (« [1, 3, 5] ») */
const LIST_SIGNS = ['[', ']', ','];
const OPS = new Set(['=', '≈', ';', '(', ')', ...LIST_SIGNS, ...CMPS, ...ARITH]);

/** clé d'un appel : « pgcd(84,36) » (espaces ôtés) */
const callKey = (atom: string): string => atom.replace(/\s+/g, '');

/** Les bouts d'une ligne de trace écrite en texte : « i = 2 ; s = 3 », « pgcd(84, 36) = 12 ». */
export function kprogLineAtoms(text: string): string[] {
  const out: string[] = [];
  const s = text.trim();
  let i = 0;
  const prevIsOp = () => !out.length || OPS.has(out[out.length - 1]!);
  while (i < s.length) {
    const c = s[i]!;
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (IDENT.test(c)) {
      let j = i + 1;
      while (j < s.length && IDENT_REST.test(s[j]!)) j++;
      let k = j;
      while (k < s.length && s[k] === ' ') k++;
      if (s[k] === '(' && !BOOLS.includes(s.slice(i, j))) {
        let depth = 0;
        let m = k;
        for (; m < s.length; m++) {
          if (s[m] === '(' || s[m] === '[') depth++;
          if (s[m] === ')' || s[m] === ']') depth--;
          if (depth === 0) break;
        }
        if (m >= s.length) throw new ProgLineError('Parenthèse non fermée.');
        out.push(s.slice(i, j) + s.slice(k, m + 1).replace(/-/g, '−'));
        i = m + 1;
        continue;
      }
      out.push(s.slice(i, j));
      i = j;
      continue;
    }
    const neg = (c === '−' || c === '-') && /\d/.test(s[i + 1] ?? '') && prevIsOp();
    if (/\d/.test(c) || neg) {
      const m = /^[−-]?\d+([.,]\d+)?/.exec(s.slice(i))!;
      out.push(m[0].replace('-', '−').replace('.', ','));
      i += m[0].length;
      continue;
    }
    const two = s.slice(i, i + 2);
    const map2: Record<string, string> = { '<=': '≤', '>=': '≥', '!=': '≠', '==': '=' };
    if (map2[two]) {
      out.push(map2[two]!);
      i += 2;
      continue;
    }
    const map1: Record<string, string> = { '-': '−', '*': '×', '/': '÷' };
    const op = map1[c] ?? c;
    if (!OPS.has(op)) throw new ProgLineError(`« ${c} » ne s’écrit pas sur une trace.`);
    out.push(op);
    i++;
  }
  return out;
}

type PExpr =
  | { k: 'num'; q: Rat }
  /** moteur v52 : une liste écrite */
  | { k: 'list'; items: PExpr[] }
  | { k: 'bool'; v: boolean }
  | { k: 'none' }
  | { k: 'var'; name: string }
  | { k: 'call'; atom: string }
  | { k: 'neg'; e: PExpr }
  | { k: 'bin'; op: string; l: PExpr; r: PExpr };

interface PClaim {
  /** nom de variable, ou appel « pgcd(84, 36) » */
  target: string;
  call: boolean;
  value: PValue;
  approx: boolean;
  places: number;
}
type PValue =
  | { k: 'num'; q: Rat }
  | { k: 'bool'; v: boolean }
  | { k: 'none' }
  /** moteur v52 */
  | { k: 'list'; items: PValue[] };

type PLine =
  | { kind: 'claims'; claims: PClaim[] }
  | { kind: 'cmp'; l: PExpr; op: CmpSym; r: PExpr }
  /** moteur v29 : un calcul du programme, « n % 2 = 1 » */
  | { kind: 'calc'; e: Extract<PExpr, { k: 'bin' }>; value: PValue };

function numAtom(a: string): Rat {
  return Q.parseDecimal(a)!;
}

/** Expression d'une ligne (nombres, variables, appels, + − × ÷, parenthèses). */
function parseExprAtoms(A: string[], allowNames: boolean): PExpr {
  let i = 0;
  const peek = () => A[i];
  const primary = (): PExpr => {
    const a = A[i];
    if (a === undefined) throw new Incomplete();
    if (a === '(') {
      i++;
      const e = sum();
      if (A[i] === undefined) throw new Incomplete();
      if (A[i] !== ')') throw new ProgLineError('Parenthèse non fermée.');
      i++;
      return e;
    }
    if (a === '−') {
      i++;
      return { k: 'neg', e: primary() };
    }
    // moteur v52 : une liste « [1, 3, 5] », « [] »
    if (a === '[') {
      i++;
      const items: PExpr[] = [];
      if (A[i] === ']') {
        i++;
        return { k: 'list', items };
      }
      for (;;) {
        items.push(sum());
        const s = A[i];
        if (s === undefined) throw new Incomplete();
        i++;
        if (s === ']') return { k: 'list', items };
        if (s !== ',') throw new ProgLineError('Dans une liste, « , » sépare deux éléments.');
      }
    }
    if (a === ']' || a === ',') throw new ProgLineError(`« ${a} » est mal placé.`);
    i++;
    if (NUM_RE.test(a)) return { k: 'num', q: numAtom(a) };
    if (a === 'None') return { k: 'none' };
    if (BOOLS.includes(a)) return { k: 'bool', v: a === 'True' };
    if (CALL_RE.test(a)) {
      if (!allowNames) throw new ProgLineError('Écris la valeur, pas un appel.');
      return { k: 'call', atom: a };
    }
    if (NAME_RE.test(a)) {
      if (!allowNames) throw new ProgLineError(`Écris la valeur de la variable, pas « ${a} ».`);
      return { k: 'var', name: a };
    }
    throw new ProgLineError(`« ${a} » est mal placé.`);
  };
  const prod = (): PExpr => {
    let l = primary();
    while (peek() === '×' || peek() === '÷' || peek() === '%') {
      const op = A[i++]!;
      l = { k: 'bin', op, l, r: primary() };
    }
    return l;
  };
  const sum = (): PExpr => {
    let l = prod();
    while (peek() === '+' || peek() === '−') {
      const op = A[i++]!;
      l = { k: 'bin', op, l, r: prod() };
    }
    return l;
  };
  const e = sum();
  if (i < A.length) throw new ProgLineError(`« ${A[i]} » est mal placé.`);
  return e;
}

/** reste de la division de Python (signe du diviseur) : 7 % 2 = 1, −7 % 2 = 1 */
function pyMod(x: Rat, y: Rat): Rat | null {
  const r = Q.div(x, y);
  if (!r) return null;
  const fl = r.n >= 0n ? r.n / r.d : -((-r.n + r.d - 1n) / r.d);
  return Q.sub(x, Q.mul(y, Q.rat(fl)));
}
function arithOp(op: string, l: Rat, r: Rat): Rat | null {
  return op === '+'
    ? Q.add(l, r)
    : op === '−'
      ? Q.sub(l, r)
      : op === '×'
        ? Q.mul(l, r)
        : op === '%'
          ? pyMod(l, r)
          : Q.div(l, r);
}

/** Valeur d'une expression sans variable (le membre de droite d'un « s = 3 + 2 »). */
function constValue(e: PExpr): PValue | null {
  if (e.k === 'bool' || e.k === 'none') return e;
  if (e.k === 'list') {
    const items = e.items.map(constValue);
    return items.every((x) => x !== null) ? { k: 'list', items: items as PValue[] } : null;
  }
  if (e.k === 'num') return e;
  if (e.k === 'neg') {
    const v = constValue(e.e);
    return v?.k === 'num' ? { k: 'num', q: Q.neg(v.q) } : null;
  }
  if (e.k === 'bin') {
    const l = constValue(e.l);
    const r = constValue(e.r);
    if (l?.k !== 'num' || r?.k !== 'num') return null;
    const q = arithOp(e.op, l.q, r.q);
    return q ? { k: 'num', q } : null;
  }
  return null;
}

/** Bouts → ligne lue. `Incomplete` : la ligne n'est pas finie ; `ProgLineError` : elle est fausse. */
function readLine(A: string[]): PLine {
  if (!A.length) throw new Incomplete();
  const last = A[A.length - 1]!;
  if (
    last === ';' ||
    last === '=' ||
    last === '≈' ||
    last === '[' ||
    last === ',' ||
    CMPS.includes(last as CmpSym) ||
    ARITH.includes(last) ||
    last === '('
  )
    throw new Incomplete();
  const cmpAt = A.findIndex((a) => CMPS.includes(a as CmpSym));
  if (cmpAt >= 0) {
    if (A.includes(';') || A.includes('=') || A.includes('≈'))
      throw new ProgLineError('Une comparaison s’écrit seule sur sa ligne.');
    if (A.slice(cmpAt + 1).some((a) => CMPS.includes(a as CmpSym)))
      throw new ProgLineError('Une seule comparaison par ligne.');
    if (cmpAt === 0) throw new ProgLineError('Écris d’abord ce que tu compares.');
    return {
      kind: 'cmp',
      l: parseExprAtoms(A.slice(0, cmpAt), true),
      op: A[cmpAt] as CmpSym,
      r: parseExprAtoms(A.slice(cmpAt + 1), true),
    };
  }
  const segs: string[][] = [[]];
  A.forEach((a) => (a === ';' ? segs.push([]) : segs[segs.length - 1]!.push(a)));
  // moteur v29 : un calcul seul sur sa ligne, « n % 2 = 1 », « 2 × 3 + 1 = 7 »
  if (segs.length === 1) {
    const seg = segs[0]!;
    const eq = seg.findIndex((a) => a === '=' || a === '≈');
    if (eq < 0 && seg.length > 1) {
      // « n % 2 » : le calcul est en cours d'écriture
      try {
        parseExprAtoms(seg, true);
      } catch (e) {
        if (e instanceof Incomplete) throw e;
        throw new ProgLineError('Écris « variable = valeur », ou un calcul « n % 2 = 1 ».');
      }
      throw new Incomplete();
    }
    if (eq > 1) {
      if (seg[eq] === '≈') throw new ProgLineError('Un calcul du programme s’écrit avec « = ».');
      const e = parseExprAtoms(seg.slice(0, eq), true);
      if (e.k !== 'bin')
        throw new ProgLineError('Une variable (ou un appel), puis « = » et sa valeur.');
      const rest = seg.slice(eq + 1);
      if (rest.some((a) => a === '=' || a === '≈'))
        throw new ProgLineError('Un seul « = » dans un calcul.');
      const value = constValue(parseExprAtoms(rest, false));
      if (!value) throw new ProgLineError('Valeur impossible à calculer.');
      return { kind: 'calc', e, value };
    }
  }
  const claims = segs.map((seg): PClaim => {
    if (!seg.length) throw new ProgLineError('Rien entre deux « ; ».');
    const eq = seg.findIndex((a) => a === '=' || a === '≈');
    if (eq < 0) {
      if (seg.length === 1 && (NAME_RE.test(seg[0]!) || CALL_RE.test(seg[0]!)))
        throw new Incomplete();
      throw new ProgLineError('Écris « variable = valeur ».');
    }
    if (eq !== 1) throw new ProgLineError('Une variable (ou un appel), puis « = » et sa valeur.');
    const target = seg[0]!;
    const call = CALL_RE.test(target);
    if (!call && (!NAME_RE.test(target) || BOOLS.includes(target)))
      throw new ProgLineError('Une variable (ou un appel), puis « = » et sa valeur.');
    const rest = seg.slice(2);
    if (rest.some((a) => a === '=' || a === '≈'))
      throw new ProgLineError('Une seule valeur par variable : sépare par « ; ».');
    const approx = seg[1] === '≈';
    if (approx) {
      if (rest.length !== 1 || !NUM_RE.test(rest[0]!))
        throw new ProgLineError('Après « ≈ », un nombre arrondi.');
      const places = rest[0]!.includes(',') ? rest[0]!.split(',')[1]!.length : 0;
      return { target, call, value: { k: 'num', q: numAtom(rest[0]!) }, approx, places };
    }
    const v = constValue(parseExprAtoms(rest, false));
    if (!v) throw new ProgLineError('Valeur impossible à calculer.');
    return { target, call, value: v, approx: false, places: 0 };
  });
  const seen = new Set<string>();
  claims.forEach((c) => {
    const k = c.call ? callKey(c.target) : c.target;
    if (seen.has(k)) throw new ProgLineError(`« ${c.target} » écrit deux fois.`);
    seen.add(k);
  });
  return { kind: 'claims', claims };
}

function valueKey(v: PValue): string {
  if (v.k === 'list') return `[${v.items.map(valueKey).join(',')}]`;
  return v.k === 'none' ? 'None' : v.k === 'bool' ? String(v.v) : Q.ratToText(v.q);
}
function lineKey(pl: PLine): string {
  if (pl.kind === 'claims')
    return pl.claims
      .map(
        (c) =>
          `${c.call ? callKey(c.target) : c.target}${c.approx ? '≈' : '='}${valueKey(c.value)}`,
      )
      .sort()
      .join(';');
  const e = (x: PExpr): string =>
    x.k === 'list'
      ? `[${x.items.map(e).join(',')}]`
      : x.k === 'num'
        ? Q.ratToText(x.q)
        : x.k === 'none'
          ? 'None'
          : x.k === 'bool'
            ? String(x.v)
            : x.k === 'var'
              ? x.name
              : x.k === 'call'
                ? callKey(x.atom)
                : x.k === 'neg'
                  ? `-(${e(x.e)})`
                  : `(${e(x.l)}${x.op}${e(x.r)})`;
  if (pl.kind === 'calc') return `calc:${e(pl.e)}=${valueKey(pl.value)}`;
  return `${e(pl.l)}${pl.op}${e(pl.r)}`;
}

/** Ligne de feuille (texte) pour l'app : l'écriture de l'élève, telle quelle. */
export function kprogLine(text: string): Line {
  return { dis: [{ sides: [{ k: 's', n: text }] }] };
}
const textOf = (A: string[]): string =>
  A.join(' ')
    .replace(/\( /g, '(')
    .replace(/ \)/g, ')')
    // moteur v52 : « [1, 3, 5] »
    .replace(/\[ /g, '[')
    .replace(/ \]/g, ']')
    .replace(/ ,/g, ',');

/* ------------------------------ état ------------------------------ */

export interface KProgState {
  /** trace (moteur v28) ; `complete` : écrire la ligne manquante (moteur v29) */
  mode: 'trace' | 'complete';
  spec: KernelSpec;
  code: string;
  call: string | null;
  trace: ProgramTrace;
  points: TracePoint[];
  last: number;
  ends: number[];
  /** variables proposées dans la palette */
  watch: string[];
  focusKey: string | null;
  cursor: number;
  lines: { key: string; text: string }[];
  condsDone: Set<number>;
  callsDone: Set<string>;
  /** calculs du programme déjà écrits (moteur v29) */
  evalsDone: Set<number>;
  /** positions atteintes, dans l'ordre (tours écrits avant un saut) */
  visited: number[];
  /** nombre de tours de chaque exécution de boucle */
  loopTours: Map<number, number>;
  /** tirages imposés (moteur v29) */
  draws: PyValue[] | undefined;
  /** objectif `complete` (moteur v29) */
  complete: CompleteState | null;
  /** cibles écrites au dernier point */
  atLast: Set<string>;
  concluded: boolean;
  answer: PClaim[];
  traps: { key: string; title: string; why: string; line: Line }[];
  path: string[];
  known: string[][] | null;
  callCache: Map<string, PyValue | null>;
  /** noms des fonctions du programme */
  funcs: Set<string>;
  /** toutes les variables de la fonction suivie */
  names: Set<string>;
}

export function programOf(model: ScratchModel): NonNullable<KernelSpec['program']> {
  const p = model.kernel?.program;
  if (!p) throw new Error('Feuille Python sans programme');
  return p;
}

/** un tirage imposé, écrit comme l'élève l'écrit : « 4 », « 0,25 », « True » */
export function parseDraw(t: string): PyValue | null {
  const s = t.trim();
  if (s === 'True' || s === 'False') return pyBool(s === 'True');
  if (/^[−-]?\d+$/.test(s)) return pyInt(BigInt(s.replace('−', '-')));
  if (/^[−-]?\d+,\d+$/.test(s)) {
    const q = Q.parseDecimal(s.replace('-', '−'));
    return q ? pyFloat(q) : null;
  }
  return null;
}
function drawsOf(model: ScratchModel): PyValue[] | undefined {
  const d = model.kernel?.program?.draws;
  if (!d) return undefined;
  return d.map((t) => {
    const v = parseDraw(t);
    if (!v) throw new Error(`Tirage illisible : « ${t} »`);
    return v;
  });
}

export function createKProgState(model: ScratchModel): KProgState {
  const spec = model.kernel!;
  const prog = programOf(model);
  if (spec.goal.kind === 'complete') return createCompleteState(model);
  const draws = drawsOf(model);
  const trace = runProgram(prog.code, { ...(prog.call ? { call: prog.call } : {}), draws });
  if (!trace.ok)
    throw new Error(`Programme : ${trace.error!.message} (ligne ${trace.error!.line})`);
  const points = trace.points;
  const ends = points.flatMap((p, i) => (p.end ? [i] : []));
  const names = new Set<string>();
  points.forEach((p) => Object.keys(p.vars).forEach((n) => names.add(n)));
  const funcs = new Set(
    [...prog.code.matchAll(/^\s*def\s+([A-Za-z_À-ɏ][\wÀ-ɏ]*)/gm)].map((m) => m[1]!),
  );
  const answerLine = readLine(kprogLineAtoms(spec.answer));
  if (answerLine.kind !== 'claims') throw new Error('La réponse s’écrit « variable = valeur ».');
  const loopTours = new Map<number, number>();
  points.forEach((p) => {
    if (p.unit === 'iteration' && p.loop) loopTours.set(p.loop, (loopTours.get(p.loop) ?? 0) + 1);
  });
  const ks: KProgState = {
    mode: 'trace',
    spec,
    code: prog.code,
    call: prog.call ?? null,
    trace,
    points,
    last: points.length - 1,
    ends,
    watch: prog.watch ?? defaultWatch(points),
    focusKey: trace.focus ? callKey(trace.focus.atom) : null,
    cursor: 0,
    lines: [],
    condsDone: new Set(),
    callsDone: new Set(),
    evalsDone: new Set(),
    visited: [0],
    loopTours,
    draws,
    complete: null,
    atLast: new Set(),
    concluded: false,
    answer: answerLine.claims,
    traps: [],
    path: spec.path ?? [],
    known: null,
    callCache: new Map(),
    funcs,
    names,
  };
  ks.traps = (model.traps ?? []).flatMap((t) => {
    try {
      return [
        {
          key: lineKey(readLine(kprogLineAtoms(t.line))),
          title: t.title,
          why: t.why,
          line: kprogLine(t.line),
        },
      ];
    } catch {
      return [];
    }
  });
  const extra = [
    spec.answer,
    ...ks.path,
    ...(model.traps ?? []).map((t) => t.line),
    ...(model.choices ?? []).flatMap((c) => (c.line ? [c.line] : [])),
  ];
  ks.known = extra.flatMap((t) => {
    try {
      return [kprogLineAtoms(t)];
    } catch {
      return [];
    }
  });
  return ks;
}

/** variables nombres ou booléens qui changent pendant l'exécution, dans l'ordre d'apparition */
function defaultWatch(points: TracePoint[]): string[] {
  const out: string[] = [];
  const scalar = (v: PyValue | undefined) =>
    !!v && (v.t === 'int' || v.t === 'float' || v.t === 'bool');
  points.forEach((p, i) => {
    Object.keys(p.vars).forEach((n) => {
      if (out.includes(n) || !scalar(p.vars[n])) return;
      const prev = i ? points[i - 1]!.vars[n] : undefined;
      const changed = !prev || formatValue(prev) !== formatValue(p.vars[n]!);
      if (i === 0 && points[0]!.kind === 'start') return;
      if (changed) out.push(n);
    });
  });
  return out;
}

/* ------------------------------ vérité ------------------------------ */

/** moteur v52 : une liste écrite contre la valeur du programme */
function matchesWritten(v: PyValue, w: PValue): boolean {
  if (w.k === 'list')
    return (
      (v.t === 'list' || v.t === 'tuple') &&
      v.items.length === w.items.length &&
      v.items.every((x, i) => matchesWritten(x, w.items[i]!))
    );
  if (w.k === 'none') return v.t === 'none';
  if (w.k === 'bool') return v.t === 'bool' && v.v === w.v;
  if (v.t !== 'int' && v.t !== 'float') return false;
  return Q.eq(numOf(v)!, w.q);
}

function valueMatches(v: PyValue | undefined, c: PClaim): boolean {
  if (!v) return false;
  if (c.value.k === 'list') return matchesWritten(v, c.value);
  if (c.value.k === 'none') return v.t === 'none';
  if (c.value.k === 'bool') return v.t === 'bool' && v.v === c.value.v;
  if (v.t !== 'int' && v.t !== 'float') return false;
  const q = numOf(v)!;
  if (!c.approx) return Q.eq(q, c.value.q);
  return Q.isRoundingOf(Q.fromRat(q), c.value.q, c.places);
}

function claimHoldsAt(ks: KProgState, c: PClaim, k: number): boolean {
  const p = ks.points[k]!;
  if (c.call) return p.kind === 'return' && valueMatches(p.ret, c);
  return valueMatches(p.vars[c.target], c);
}
const holdsAt = (ks: KProgState, cs: PClaim[], k: number): boolean =>
  cs.every((c) => claimHoldsAt(ks, c, k));

function nextEnd(ks: KProgState, c: number): number {
  return ks.ends.find((e) => e > c) ?? Infinity;
}
function prevEnd(ks: KProgState, c: number): number {
  let out = 0;
  ks.ends.forEach((e) => {
    if (e < c) out = e;
  });
  return out;
}

/** appel d'une fonction du programme : valeur exacte (mise en cache) */
function callValue(ks: KProgState, atom: string): PyValue | null {
  const key = callKey(atom);
  if (ks.callCache.has(key)) return ks.callCache.get(key)!;
  const m = CALL_RE.exec(atom)!;
  const inner = m[2]!;
  // « f(1,5) » → f(1.5) ; « g(−4) » → g(-4) ; les virgules suivies d'une espace séparent
  const py = `${m[1]}(${inner.replace(/−/g, '-').replace(/(\d),(\d)/g, '$1.$2')})`;
  const v = ks.funcs.has(m[1]!)
    ? evalCall(ks.code, py, undefined, ks.draws ? { draws: ks.draws } : {})
    : null;
  ks.callCache.set(key, v);
  return v;
}

function exprValue(ks: KProgState, e: PExpr, vars: Record<string, PyValue>): Rat | boolean | null {
  switch (e.k) {
    case 'list':
      return null;
    case 'num':
      return e.q;
    case 'none':
      return null;
    case 'bool':
      return e.v;
    case 'var': {
      const v = vars[e.name];
      if (!v) return null;
      if (v.t === 'bool') return v.v;
      return numOf(v);
    }
    case 'call': {
      const v =
        callKey(e.atom) === ks.focusKey ? (ks.trace.focus?.value ?? null) : callValue(ks, e.atom);
      if (!v) return null;
      if (v.t === 'bool') return v.v;
      return numOf(v);
    }
    case 'neg': {
      const v = exprValue(ks, e.e, vars);
      return v !== null && typeof v !== 'boolean' ? Q.neg(v) : null;
    }
    case 'bin': {
      const l = exprValue(ks, e.l, vars);
      const r = exprValue(ks, e.r, vars);
      if (l === null || r === null || typeof l === 'boolean' || typeof r === 'boolean') return null;
      return arithOp(e.op, l, r);
    }
  }
}

const PY_CMP: Record<string, CmpSym | '='> = {
  '<': '<',
  '>': '>',
  '<=': '≤',
  '>=': '≥',
  '!=': '≠',
  '==': '=',
};
const NEG: Record<string, CmpSym | '='> = {
  '<': '≥',
  '>': '≤',
  '≤': '>',
  '≥': '<',
  '≠': '=',
  '=': '≠',
};
const FLIP: Record<string, CmpSym | '='> = {
  '<': '>',
  '>': '<',
  '≤': '≥',
  '≥': '≤',
  '≠': '≠',
  '=': '=',
};

function cmpHolds(op: CmpSym | '=', a: Rat | boolean, b: Rat | boolean): boolean {
  if (typeof a === 'boolean' || typeof b === 'boolean')
    return op === '=' ? a === b : op === '≠' ? a !== b : false;
  const c = Q.cmp(a, b);
  return op === '<'
    ? c < 0
    : op === '>'
      ? c > 0
      : op === '≤'
        ? c <= 0
        : op === '≥'
          ? c >= 0
          : op === '≠'
            ? c !== 0
            : c === 0;
}
const sameValue = (a: Rat | boolean | null, v: PyValue): boolean => {
  if (a === null) return false;
  if (typeof a === 'boolean') return v.t === 'bool' && v.v === a;
  const q = numOf(v);
  return !!q && v.t !== 'bool' && Q.eq(a, q);
};

/** la comparaison écrite dit-elle l'issue du test `e` ? (valeurs des membres, sens) */
function saysOutcome(
  e: CondEvent,
  op: CmpSym | '=',
  l: Rat | boolean | null,
  r: Rat | boolean | null,
): boolean {
  if (!e.cmp) return false;
  const pop = PY_CMP[e.cmp.op as CmpOp];
  if (!pop) return false;
  const said = e.result ? pop : NEG[pop]!;
  if (sameValue(l, e.cmp.l) && sameValue(r, e.cmp.r) && op === said) return true;
  return sameValue(l, e.cmp.r) && sameValue(r, e.cmp.l) && op === FLIP[said];
}

/* ------------------------------ jugement ------------------------------ */

function knownAtoms(ks: KProgState): string[][] {
  return ks.known ?? [];
}
function strictPrefixOfKnown(ks: KProgState, A: string[]): boolean {
  return knownAtoms(ks).some((k) => k.length > A.length && A.every((a, i) => k[i] === a));
}

export function kprogJudge(
  ks: KProgState,
  chips: Chip[],
  opts?: { verify?: boolean },
): KProgVerdict {
  const A = chips.flatMap((c) => c.atoms);
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  if (ks.mode === 'complete') return judgeComplete(ks, A, opts);
  const prefix = strictPrefixOfKnown(ks, A);
  let pl: PLine;
  try {
    pl = readLine(A);
  } catch (e) {
    if (e instanceof Incomplete || prefix)
      return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: e instanceof Error ? e.message : String(e),
      badChip: chips.length - 1,
    };
  }
  const v = evaluate(ks, pl, A);
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready' };
    return { kind: 'building', atoms: A, complete: true };
  }
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return v;
}

const valueText = (v: PValue): string =>
  v.k === 'list'
    ? `[${v.items.map(valueText).join(', ')}]`
    : v.k === 'none'
      ? 'None'
      : v.k === 'bool'
        ? v.v
          ? 'True'
          : 'False'
        : formatValue({ t: 'float', v: v.q }).replace(/^(-?\d+),0$/, '$1');
const claimText = (c: PClaim): string =>
  `${c.target} ${c.approx ? '≈' : '='} ${valueText(c.value)}`;
const claimsText = (cs: PClaim[]): string => cs.map(claimText).join(' ; ');

const UNIT_LABEL: Record<string, { id: string; label: string; why: (s: string) => string }> = {
  block: {
    id: PYTHON_STEP_IDS.block,
    label: 'Exécuter les instructions',
    why: (s) => `Les instructions s’exécutent dans l’ordre : ${s}.`,
  },
  iteration: {
    id: PYTHON_STEP_IDS.iteration,
    label: 'Un tour de boucle',
    why: (s) => `Après ce tour de boucle : ${s}.`,
  },
  branch: {
    id: PYTHON_STEP_IDS.branch,
    label: 'Exécuter le bloc',
    why: (s) => `Le test choisit le bloc à exécuter : ${s}.`,
  },
  return: {
    id: PYTHON_STEP_IDS.return,
    label: 'La valeur renvoyée',
    why: (s) => `La fonction renvoie sa valeur : ${s}.`,
  },
};

function evaluate(ks: KProgState, pl: PLine, A: string[]): KProgVerdict {
  const text = textOf(A);
  const line = kprogLine(text);
  const key = lineKey(pl);
  const base = { atoms: A, line };
  if (pl.kind === 'cmp') return evaluateCmp(ks, pl, key, base);
  if (pl.kind === 'calc') return evaluateCalc(ks, pl, key, base);
  // grandeurs connues du programme
  for (const c of pl.claims) {
    if (c.call) {
      const m = CALL_RE.exec(c.target)!;
      if (callKey(c.target) !== ks.focusKey && !ks.funcs.has(m[1]!))
        return {
          kind: 'false',
          ...base,
          title: 'Fonction inconnue',
          message: `Le programme n’a pas de fonction « ${m[1]} ».`,
        };
    } else if (!ks.names.has(c.target))
      return {
        kind: 'false',
        ...base,
        title: 'Variable inconnue',
        message: `Le programme n’a pas de variable « ${c.target} ».`,
      };
  }
  const calls = pl.claims.filter((c) => c.call && callKey(c.target) !== ks.focusKey);
  if (calls.length && calls.length < pl.claims.length)
    return {
      kind: 'syntax',
      ...base,
      title: 'Une ligne pour l’appel',
      message: 'Écris le calcul d’un appel de fonction sur sa propre ligne.',
      badChip: -1,
    };
  if (calls.length) return evaluateCalls(ks, calls, key, base);
  return evaluateState(ks, pl.claims, key, base);
}

function trapOrFalse(
  ks: KProgState,
  key: string,
  base: { atoms: string[]; line: Line },
  title: string,
  message: string,
): KProgVerdict {
  const trap = ks.traps.find((t) => t.key === key);
  if (trap)
    return {
      kind: 'trap',
      ...base,
      title: 'Piège : ' + trap.title.charAt(0).toLowerCase() + trap.title.slice(1),
      message: trap.why,
      trap: { line: trap.line, title: trap.title, why: trap.why },
      badChip: -1,
    };
  return { kind: 'false', ...base, title, message };
}

function evaluateState(
  ks: KProgState,
  claims: PClaim[],
  key: string,
  base: { atoms: string[]; line: Line },
): KProgVerdict {
  const c = ks.cursor;
  const targets = claims.map((x) => (x.call ? callKey(x.target) : x.target));
  if (holdsAt(ks, claims, c)) {
    // « b = 0 » : l'issue d'un test « b != 0 » (ou « b == 0 »)
    if (claims.length === 1 && !claims[0]!.call && !claims[0]!.approx) {
      const cv0 = claims[0]!.value;
      const cv =
        cv0.k === 'none' || cv0.k === 'list'
          ? null
          : evaluateCmpLike(
              ks,
              { k: 'var', name: claims[0]!.target },
              '=',
              cv0.k === 'bool' ? { k: 'bool', v: cv0.v } : { k: 'num', q: cv0.q },
              key,
              base,
            );
      if (cv) return cv;
    }
    if (c === ks.last && coversAnswer(ks, claims)) {
      if (ks.concluded)
        return {
          kind: 'useless',
          ...base,
          title: 'Tu as déjà conclu',
          message: 'Le programme est fini et ta réponse est écrite.',
          pmove: { key },
        };
      return {
        kind: 'step',
        ...base,
        final: true,
        hora: 1,
        rule: {
          id: PYTHON_STEP_IDS.conclude,
          card: CARD,
          label: 'Conclure',
          hora: 1,
          why: `Le programme est fini : ${claimsText(claims)}.`,
        },
        pmove: { key, conclude: true, to: c, targets },
      };
    }
    if (ks.answer.length && c < ks.last && sameTargets(ks, claims) && holdsAt(ks, claims, ks.last))
      return {
        kind: 'useless',
        ...base,
        title: 'Pas encore fini',
        message: 'C’est vrai pour l’instant, mais le programme continue : trace la suite.',
        pmove: { key },
      };
    return {
      kind: 'useless',
      ...base,
      title: 'Rien de nouveau',
      message: 'C’est déjà l’état du programme à ta dernière ligne : écris ce qui change ensuite.',
      pmove: { key },
    };
  }
  let k = -1;
  for (let j = c + 1; j < ks.points.length; j++)
    if (holdsAt(ks, claims, j)) {
      k = j;
      break;
    }
  if (k < 0) {
    if (claims.some((x) => !x.approx && x.value.k === 'num') && approxWouldHold(ks, claims))
      return {
        kind: 'false',
        ...base,
        title: 'Valeur approchée',
        message:
          'Le programme calcule une valeur avec plus de décimales : écris « ≈ » pour une valeur arrondie.',
      };
    for (let j = 0; j < c; j++)
      if (holdsAt(ks, claims, j))
        return trapOrFalse(
          ks,
          key,
          base,
          'C’était avant',
          'Ces valeurs, c’était plus tôt dans le programme. Écris l’état qui suit ta dernière ligne.',
        );
    const never = claims.find(
      (x) => !ks.points.slice(c + 1).some((_, i) => claimHoldsAt(ks, x, c + 1 + i)),
    );
    return trapOrFalse(
      ks,
      key,
      base,
      'Ce n’est pas ce que fait le programme',
      never
        ? never.call
          ? `${never.target} ne renvoie pas ${claimText(never).split(/ [=≈] /)[1]}.`
          : `Après ta dernière ligne, ${never.target} ne vaut jamais ${claimText(never).split(/ [=≈] /)[1]}.`
        : 'Ces valeurs ne sont jamais vraies en même temps : relis l’ordre des instructions.',
    );
  }
  const end = nextEnd(ks, c);
  // « k = 1 ; f = 1 » : vrai au milieu du tour ET à sa fin (f × 1 = f) ; la ligne qui écrit
  // tout ce qui change pendant l'unité la termine
  if (k < end && end !== Infinity && holdsAt(ks, claims, end) && writesChanges(ks, claims, c, end))
    k = end;
  let jump = 0;
  if (k > end) {
    const p = ks.points[end]!;
    const j = jumpCheck(ks, claims, c, end, k);
    if (j.ok) jump = j.tours;
    else
      return {
        kind: 'unjustified',
        ...base,
        title: j.title ?? (p.unit === 'iteration' ? 'Un tour à la fois' : 'Une étape à la fois'),
        message:
          j.message ??
          (p.unit === 'iteration'
            ? 'Ta ligne sera juste plus tard : écris d’abord l’état à la fin du tour suivant.'
            : 'Ta ligne sera juste plus tard : écris d’abord ce que fait l’étape suivante du programme.'),
      };
  }
  const fromEnd = ks.points[c]!.end;
  const isEnd = k === end || jump > 0;
  const unit = isEnd ? (ks.points[k]!.unit ?? 'block') : null;
  const hora = isEnd && fromEnd ? 2 : 1;
  const u = jump
    ? {
        id: PYTHON_STEP_IDS.jump,
        label: 'Sauter des tours',
        why: (t: string) => `Tu avances de ${jump} tours d’un coup : à la fin de ce tour, ${t}.`,
      }
    : unit
      ? UNIT_LABEL[unit]!
      : null;
  const covers = k === ks.last && coversAnswer(ks, claims);
  const exitPending =
    k === ks.last &&
    ks.trace.conds.some((e, i) => e.after === ks.last && e.loop && !ks.condsDone.has(i));
  const final = covers && !exitPending && !ks.concluded;
  const why =
    (u ? u.why(claimsText(claims)) : `Une instruction exécutée : ${claimsText(claims)}.`) +
    (final ? ' Le programme est fini.' : '');
  return {
    kind: 'step',
    ...base,
    hora,
    final,
    rule: u
      ? { id: u.id, card: CARD, label: u.label, hora, why }
      : { id: PYTHON_STEP_IDS.assign, card: CARD, label: 'Une affectation', hora, why },
    pmove: { key, to: k, targets, ...(final ? { conclude: true } : {}) },
  };
}

/**
 * Sauter des tours (moteur v29) : seulement dans une boucle de PYTHON_JUMP_MIN_TOURS tours
 * ou plus, une fois deux tours de cette boucle écrits en entier, jusqu'à la FIN d'un tour
 * plus loin de la même boucle, l'état complet (toutes les variables qui changent).
 */
function jumpCheck(
  ks: KProgState,
  claims: PClaim[],
  c: number,
  end: number,
  k: number,
): { ok: true; tours: number } | { ok: false; title?: string; message?: string } {
  const pe = ks.points[end]!;
  const loop = pe.unit === 'iteration' ? pe.loop : undefined;
  if (!loop || (ks.loopTours.get(loop) ?? 0) < PYTHON_JUMP_MIN_TOURS) return { ok: false };
  // la fin du tour où la ligne devient vraie
  const e = ks.ends.find((x) => x >= k) ?? -1;
  const pk = e >= 0 ? ks.points[e]! : null;
  if (!pk || pk.unit !== 'iteration' || pk.loop !== loop) return { ok: false };
  const written = ks.visited.filter((v) => {
    const p = ks.points[v]!;
    return p.unit === 'iteration' && p.loop === loop;
  }).length;
  if (written < JUMP_AFTER_TOURS)
    return {
      ok: false,
      title: 'Un tour à la fois',
      message:
        'La boucle est longue : écris d’abord deux tours en entier, ensuite tu pourras sauter des tours.',
    };
  if (e !== k || !holdsAt(ks, claims, e))
    return {
      ok: false,
      title: 'À la fin d’un tour',
      message: 'Pour sauter des tours, écris l’état à la FIN d’un tour, pas au milieu.',
    };
  if (!writesChanges(ks, claims, c, e))
    return {
      ok: false,
      title: 'L’état complet',
      message:
        'Pour sauter des tours, écris toutes les variables qui changent pendant la boucle, séparées par « ; ».',
    };
  const tours = ks.points.filter(
    (p, i) => i > c && i <= e && p.unit === 'iteration' && p.loop === loop,
  ).length;
  return { ok: true, tours };
}

/** la ligne écrit-elle chaque variable suivie qui change entre les points a et b ? */
function writesChanges(ks: KProgState, claims: PClaim[], a: number, b: number): boolean {
  const written = new Set(claims.filter((x) => !x.call).map((x) => x.target));
  const va = ks.points[a]!.vars;
  const vb = ks.points[b]!.vars;
  const key = (v: PyValue | undefined) => (v ? formatValue(v, true) : '∅');
  return ks.watch.every((n) => key(va[n]) === key(vb[n]) || written.has(n));
}

function sameTargets(ks: KProgState, claims: PClaim[]): boolean {
  const t = new Set(claims.map((c) => (c.call ? callKey(c.target) : c.target)));
  return ks.answer.every((a) => t.has(a.call ? callKey(a.target) : a.target));
}
function coversAnswer(ks: KProgState, claims: PClaim[]): boolean {
  return sameTargets(ks, claims) && holdsAt(ks, claims, ks.last) && holdsAt(ks, ks.answer, ks.last);
}
function approxWouldHold(ks: KProgState, claims: PClaim[]): boolean {
  const loose = claims.map((x) =>
    !x.approx && x.value.k === 'num' ? { ...x, approx: true, places: decimals(x.value.q) } : x,
  );
  for (let j = ks.cursor + 1; j < ks.points.length; j++) if (holdsAt(ks, loose, j)) return true;
  return false;
}
/** chiffres après la virgule d'un décimal écrit (« 107,37 » → 2) */
function decimals(q: Rat): number {
  let n = 0;
  let x = q;
  while (!Q.isInteger(x) && n < 30) {
    x = Q.mul(x, Q.rat(10));
    n++;
  }
  return n;
}

/** fenêtre des tests et appels écrivables : l'unité finie à la ligne précédente et la suivante */
function inWindow(ks: KProgState, after: number): boolean {
  const c = ks.cursor;
  return after >= prevEnd(ks, c) && after < nextEnd(ks, c);
}

/** noms (variables, fonctions) écrits dans une expression de la ligne */
function exprNames(e: PExpr, out = new Set<string>()): Set<string> {
  if (e.k === 'var') out.add(e.name);
  if (e.k === 'call') out.add(CALL_RE.exec(e.atom)![1]!);
  if (e.k === 'neg') exprNames(e.e, out);
  if (e.k === 'bin') {
    exprNames(e.l, out);
    exprNames(e.r, out);
  }
  return out;
}

function evaluateCmpLike(
  ks: KProgState,
  l: PExpr,
  op: CmpSym | '=',
  r: PExpr,
  key: string,
  base: { atoms: string[]; line: Line },
): KProgVerdict | null {
  const conds = ks.trace.conds;
  const written = exprNames(r, exprNames(l));
  for (let i = 0; i < conds.length; i++) {
    const e = conds[i]!;
    if (!inWindow(ks, e.after)) continue;
    // « c = 0 » ne dit rien du test « i % 2 == 0 » : les noms écrits sont ceux du test
    const inTest = new Set(e.text.match(/[A-Za-z_\u00C0-\u024F][\w\u00C0-\u024F]*/g) ?? []);
    if ([...written].some((n) => !inTest.has(n))) continue;
    const vars = ks.points[Math.max(0, e.after)]!.vars;
    const lv = exprValue(ks, l, vars);
    const rv = exprValue(ks, r, vars);
    if (!saysOutcome(e, op, lv, rv)) continue;
    if (ks.condsDone.has(i))
      return {
        kind: 'useless',
        ...base,
        title: 'Déjà testé',
        message: 'Tu as déjà écrit ce test.',
        pmove: { key },
      };
    const verdict = e.result ? 'vrai' : 'faux';
    const then = e.loop
      ? e.result
        ? 'la boucle continue'
        : 'la boucle s’arrête'
      : e.result
        ? 'on exécute le bloc du if'
        : 'on passe à la suite (else)';
    const exit = e.loop && !e.result && e.after === ks.last;
    const final =
      exit &&
      !ks.concluded &&
      ks.answer.every((a) => ks.atLast.has(a.call ? callKey(a.target) : a.target)) &&
      holdsAt(ks, ks.answer, ks.last);
    const why =
      `« ${e.text} » est ${verdict} : ${then}.` + (final ? ' Le programme est fini.' : '');
    return {
      kind: 'step',
      ...base,
      hora: 1,
      final,
      rule: {
        id: PYTHON_STEP_IDS.condition,
        card: CARD,
        label: 'Tester la condition',
        hora: 1,
        why,
      },
      pmove: { key, conds: [i], ...(final ? { conclude: true } : {}) },
    };
  }
  return null;
}

function evaluateCmp(
  ks: KProgState,
  pl: Extract<PLine, { kind: 'cmp' }>,
  key: string,
  base: { atoms: string[]; line: Line },
): KProgVerdict {
  const names = new Set<string>();
  const walk = (e: PExpr) => {
    if (e.k === 'var') names.add(e.name);
    if (e.k === 'neg') walk(e.e);
    if (e.k === 'bin') {
      walk(e.l);
      walk(e.r);
    }
  };
  walk(pl.l);
  walk(pl.r);
  const unknown = [...names].find((n) => !ks.names.has(n));
  if (unknown)
    return {
      kind: 'false',
      ...base,
      title: 'Variable inconnue',
      message: `Le programme n’a pas de variable « ${unknown} ».`,
    };
  const hit = evaluateCmpLike(ks, pl.l, pl.op, pl.r, key, base);
  if (hit) return hit;
  const vars = ks.points[ks.cursor]!.vars;
  const lv = exprValue(ks, pl.l, vars);
  const rv = exprValue(ks, pl.r, vars);
  if (lv === null || rv === null)
    return {
      kind: 'false',
      ...base,
      title: 'Je ne peux pas comparer',
      message: 'Une des valeurs n’existe pas encore à ce moment du programme.',
    };
  if (!cmpHolds(pl.op, lv, rv))
    return trapOrFalse(
      ks,
      key,
      base,
      'Comparaison fausse',
      'Avec les valeurs à ta dernière ligne, cette comparaison est fausse.',
    );
  return {
    kind: 'useless',
    ...base,
    title: 'Juste, mais à côté',
    message: 'C’est vrai, mais ce n’est pas le test que fait le programme maintenant.',
    pmove: { key },
  };
}

const PY_ARITH: Record<string, string> = { '+': '+', '−': '-', '×': '*', '÷': '/', '%': '%' };

/** texte d'une expression de la ligne (« n % 2 », « 2 × 3 + 1 ») */
function pexprText(e: PExpr): string {
  switch (e.k) {
    case 'list':
      return `[${e.items.map(pexprText).join(', ')}]`;
    case 'num':
      return formatValue({ t: 'float', v: e.q }).replace(/^(−?\d+),0$/, '$1');
    case 'none':
      return 'None';
    case 'bool':
      return e.v ? 'True' : 'False';
    case 'var':
      return e.name;
    case 'call':
      return e.atom;
    case 'neg':
      return `−${pexprText(e.e)}`;
    case 'bin':
      return `${pexprText(e.l)} ${e.op} ${pexprText(e.r)}`;
  }
}

/**
 * « n % 2 = 1 » (moteur v29) : un calcul que fait le programme maintenant. L'opération
 * et la valeur de ses deux termes (écrits avec les variables ou leurs valeurs) sont
 * celles d'un calcul noté pendant l'étape en cours.
 */
function evaluateCalc(
  ks: KProgState,
  pl: Extract<PLine, { kind: 'calc' }>,
  key: string,
  base: { atoms: string[]; line: Line },
): KProgVerdict {
  const written = exprNames(pl.e);
  const unknown = [...written].find((n) => !ks.names.has(n) && !ks.funcs.has(n));
  if (unknown)
    return {
      kind: 'false',
      ...base,
      title: 'Variable inconnue',
      message: `Le programme n’a pas de variable « ${unknown} ».`,
    };
  const op = PY_ARITH[pl.e.op];
  const said = pl.value;
  const lineText = pexprText(pl.e);
  const evals: EvalEvent[] = ks.trace.evals;
  for (let i = 0; i < evals.length; i++) {
    const e = evals[i]!;
    if (!inWindow(ks, e.after)) continue;
    if (e.op !== op && !(op === '/' && e.op === '//')) continue;
    const inText = new Set(e.text.match(/[A-Za-z_\u00C0-\u024F][\w\u00C0-\u024F]*/g) ?? []);
    if ([...written].some((n) => !inText.has(n))) continue;
    const vars = ks.points[Math.max(0, e.after)]!.vars;
    if (
      !sameValue(exprValue(ks, pl.e.l, vars), e.l) ||
      !sameValue(exprValue(ks, pl.e.r, vars), e.r)
    )
      continue;
    const ok =
      said.k === 'num'
        ? sameValue(said.q, e.v)
        : said.k === 'bool'
          ? sameValue(said.v, e.v)
          : false;
    if (!ok)
      return trapOrFalse(ks, key, base, 'Recalcule', `${lineText} ne vaut pas ${valueText(said)}.`);
    if (ks.evalsDone.has(i))
      return {
        kind: 'useless',
        ...base,
        title: 'Déjà calculé',
        message: 'Tu as déjà écrit ce calcul.',
        pmove: { key },
      };
    return {
      kind: 'step',
      ...base,
      hora: 1,
      rule: {
        id: PYTHON_STEP_IDS.eval,
        card: CARD,
        label: 'Calculer',
        hora: 1,
        why: `Le programme calcule ${e.text} : ${lineText} = ${valueText(said)}.`,
      },
      pmove: { key, evals: [i] },
    };
  }
  // pas un calcul du programme maintenant : juste, ou faux ?
  const vars = ks.points[ks.cursor]!.vars;
  const v = exprValue(ks, pl.e, vars);
  if (v === null || typeof v === 'boolean' || said.k !== 'num')
    return {
      kind: 'false',
      ...base,
      title: 'Je ne peux pas calculer',
      message: 'Une des valeurs n’existe pas encore à ce moment du programme.',
    };
  if (!Q.eq(v, said.q))
    return trapOrFalse(ks, key, base, 'Calcul faux', `${lineText} ne vaut pas ${valueText(said)}.`);
  return {
    kind: 'useless',
    ...base,
    title: 'Juste, mais à côté',
    message: 'C’est juste, mais le programme ne fait pas ce calcul maintenant.',
    pmove: { key },
  };
}

function evaluateCalls(
  ks: KProgState,
  calls: PClaim[],
  key: string,
  base: { atoms: string[]; line: Line },
): KProgVerdict {
  for (const c of calls) {
    const v = callValue(ks, c.target);
    if (!v)
      return {
        kind: 'false',
        ...base,
        title: 'Appel impossible',
        message: `Je ne sais pas calculer ${c.target} avec ce programme.`,
      };
    if (!valueMatches(v, c))
      return trapOrFalse(
        ks,
        key,
        base,
        'Recalcule',
        `${c.target} ne vaut pas ${claimText(c).split(/ [=≈] /)[1]}.`,
      );
  }
  const fresh = calls.filter(
    (c) =>
      !ks.callsDone.has(callKey(c.target)) &&
      ks.trace.calls.some((e) => callKey(e.atom) === callKey(c.target) && inWindow(ks, e.after)),
  );
  if (!fresh.length)
    return calls.every((c) => ks.callsDone.has(callKey(c.target)))
      ? {
          kind: 'useless',
          ...base,
          title: 'Déjà calculé',
          message: 'Tu as déjà écrit ce calcul.',
          pmove: { key },
        }
      : {
          kind: 'useless',
          ...base,
          title: 'Juste, mais à côté',
          message: 'C’est juste, mais le programme ne fait pas ce calcul maintenant.',
          pmove: { key },
        };
  const hora = fresh.length;
  return {
    kind: 'step',
    ...base,
    hora,
    rule: {
      id: PYTHON_STEP_IDS.call,
      card: CARD,
      label: `Calculer ${fresh.map((c) => c.target).join(' et ')}`,
      hora,
      why: `Le programme calcule ${fresh.map((c) => c.target).join(' et ')} : ${claimsText(fresh)}.`,
    },
    pmove: { key, calls: fresh.map((c) => callKey(c.target)) },
  };
}

/* ------------------------------ feuille ------------------------------ */

export function kprogCommit(ks: KProgState, v: KProgVerdict): void {
  const m = v.pmove;
  if (!m || (v.kind !== 'step' && v.kind !== 'ready' && v.kind !== 'useless')) return;
  ks.lines.push({ key: m.key, text: v.line ? (v.line.dis[0]!.sides[0] as { n: string }).n : '' });
  if (v.kind === 'useless') return;
  if (m.to !== undefined) {
    ks.cursor = m.to;
    ks.visited.push(m.to);
    if (m.to === ks.last) (m.targets ?? []).forEach((t) => ks.atLast.add(t));
  }
  (m.conds ?? []).forEach((i) => ks.condsDone.add(i));
  (m.calls ?? []).forEach((c) => ks.callsDone.add(c));
  (m.evals ?? []).forEach((i) => ks.evalsDone.add(i));
  if (m.conclude) ks.concluded = true;
}

export function kprogGoalReached(ks: KProgState): boolean {
  return ks.concluded;
}

function readText(text: string): PLine | null {
  try {
    return readLine(kprogLineAtoms(text));
  } catch {
    return null;
  }
}

/** La ligne (texte) est-elle sur la feuille ? */
export function kprogEstablished(ks: KProgState, text: string): boolean {
  if (ks.mode === 'complete') {
    const k = codeKeyOf(text);
    return k !== null && ks.lines.some((l) => l.key === k);
  }
  const pl = readText(text);
  if (!pl) return false;
  const key = lineKey(pl);
  return ks.lines.some((l) => l.key === key);
}

/** La ligne (texte) est-elle vraie à la fin du programme ? */
export function kprogHolds(ks: KProgState, text: string): boolean {
  if (ks.mode === 'complete') return completePasses(ks, text);
  const pl = readText(text);
  return !!pl && pl.kind === 'claims' && holdsAt(ks, pl.claims, ks.last);
}

/** La ligne (texte) est-elle un état du programme, à un moment quelconque ? (pièges) */
export function kprogEverHolds(ks: KProgState, text: string): boolean {
  if (ks.mode === 'complete') return completePasses(ks, text);
  const pl = readText(text);
  if (!pl || pl.kind !== 'claims') return false;
  return ks.points.some((_, k) => holdsAt(ks, pl.claims, k));
}

/** Le texte (choix du QCM) dit-il la réponse ? (sans regarder la feuille) */
export function kprogSameAsAnswer(ks: KProgState, text: string): boolean {
  if (ks.mode === 'complete') return completePasses(ks, text);
  const pl = readText(text);
  return (
    !!pl && pl.kind === 'claims' && sameTargets(ks, pl.claims) && holdsAt(ks, pl.claims, ks.last)
  );
}

export function kprogIsAnswer(ks: KProgState, text: string): boolean {
  return ks.concluded && kprogSameAsAnswer(ks, text);
}

const chipsOf = (atoms: string[]): Chip[] => atoms.map((a) => ({ atoms: [a], text: a }));

/** Coup de pouce : la prochaine ligne du corrigé qui fait avancer la trace. */
export function kprogNextMove(
  ks: KProgState,
): { line: Line; text: string; atoms: string[]; label: string } | null {
  if (ks.concluded) return null;
  for (const text of [...ks.path, ks.spec.answer]) {
    let atoms: string[];
    try {
      atoms = ks.mode === 'complete' ? kprogCodeAtoms(text) : kprogLineAtoms(text);
    } catch {
      continue;
    }
    const v = kprogJudge(ks, chipsOf(atoms), { verify: true });
    if (v.kind === 'step')
      return { line: kprogLine(text), text, atoms, label: v.rule?.label ?? '' };
  }
  return null;
}

/** Le corrigé rejoué ligne à ligne (porte, palette). */
export function kprogSolvePath(model: ScratchModel): {
  lines: { text: string; line: Line; atoms: string[]; v: KProgVerdict }[];
  reached: boolean;
} {
  const ks = createKProgState(model);
  const lines = ks.path.map((text) => {
    const atoms = ks.mode === 'complete' ? kprogCodeAtoms(text) : kprogLineAtoms(text);
    const v = kprogJudge(ks, chipsOf(atoms), { verify: true });
    if (v.kind === 'step') kprogCommit(ks, v);
    return { text, line: kprogLine(text), atoms, v };
  });
  return { lines, reached: kprogGoalReached(ks) };
}

/** Les bouts du programme proposés dans la palette : variables suivies, appels. */
export function kprogPaletteParts(model: ScratchModel): { names: string[]; calls: string[] } {
  try {
    const ks = createKProgState(model);
    if (ks.mode === 'complete') return { names: [], calls: [] };
    const calls = new Set<string>();
    if (ks.trace.focus) calls.add(ks.trace.focus.atom);
    [ks.spec.answer, ...ks.path, ...(model.traps ?? []).map((t) => t.line)].forEach((t) => {
      try {
        kprogLineAtoms(t).forEach((a) => {
          if (CALL_RE.test(a)) calls.add(a);
        });
      } catch {
        /* ligne illisible : la porte la signale */
      }
    });
    return { names: ks.watch, calls: [...calls] };
  } catch {
    return { names: [], calls: [] };
  }
}

/* ------------------------------ compléter une ligne (moteur v29) ------------------------------ */
// Objectif `complete` (piste I4) : le programme a UNE ligne manquante, écrite « ____ » dans
// `program.code`. L'élève écrit cette ligne avec des bouts de code ; le moteur l'insère
// (même indentation) et exécute le programme complété sur les tests de l'énoncé :
// `program.checks` (« s = 5050 », « conversion(100) = 108 ») et `program.output` (ce que
// le programme doit afficher). Tout passe : c'est la réponse (3 HORA). Sinon le message
// dit ce que donne le programme avec SA ligne, jamais la bonne ligne.

/** la ligne manquante du programme : « ____ » (au moins trois tirets bas), indentée */
export const BLANK_LINE_RE = /^(\s*)_{3,}\s*$/;

export interface CompleteState {
  lines: string[];
  blank: number;
  indent: string;
  checks: { text: string; claims: PClaim[] }[];
  output: string[] | null;
  cache: Map<string, CompleteResult>;
}
type CompleteResult = { ok: true } | { ok: false; title: string; message: string };

const CODE_TOKEN =
  /(\d+\.\d+|\d+)|([A-Za-z_À-ɏ][A-Za-z0-9_À-ɏ]*)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')|(\*\*=|\/\/=|\*\*|\/\/|==|!=|<=|>=|\+=|-=|\*=|\/=|%=|[-+*/%<>=():,[\].])/y;
/** mots-clés suivis d'une espace avant une parenthèse (« while (x) ») */
const PY_KEYWORDS = new Set([
  'for',
  'in',
  'while',
  'if',
  'elif',
  'else',
  'return',
  'not',
  'and',
  'or',
  'def',
  'import',
  'from',
  'is',
  'pass',
  'break',
  'continue',
]);
const isOpToken = (t: string): boolean => /^[-+*/%<>=!]+$/.test(t);

/** Les bouts d'une ligne de code : « for i in range(1, 101): » → for, i, in, range, (, 1… */
export function kprogCodeAtoms(text: string): string[] {
  const src = text.replace(/−/g, '-').replace(/×/g, '*').replace(/÷/g, '/');
  const out: string[] = [];
  let i = 0;
  while (i < src.length) {
    if (/\s/.test(src[i]!)) {
      i++;
      continue;
    }
    CODE_TOKEN.lastIndex = i;
    const m = CODE_TOKEN.exec(src);
    if (!m) throw new ProgLineError(`« ${src[i]} » ne s’écrit pas en Python ici.`);
    out.push(m[0]);
    i = CODE_TOKEN.lastIndex;
  }
  return out;
}

/** La ligne de code écrite (espaces de Python) : « for i in range(1, 101): » */
export function codeText(atoms: string[]): string {
  let out = '';
  atoms.forEach((a, i) => {
    if (i === 0) {
      out = a;
      return;
    }
    const prev = atoms[i - 1]!;
    const prev2 = atoms[i - 2];
    let space = true;
    if (a === ')' || a === ']' || a === ':' || a === ',' || a === '.') space = false;
    else if (prev === '(' || prev === '[' || prev === '.') space = false;
    else if ((a === '(' || a === '[') && /^[A-Za-z_À-ɏ0-9\])]/.test(prev) && !PY_KEYWORDS.has(prev))
      space = false;
    else if (
      prev === '-' &&
      (prev2 === undefined ||
        isOpToken(prev2) ||
        prev2 === '(' ||
        prev2 === '[' ||
        prev2 === ',' ||
        PY_KEYWORDS.has(prev2))
    )
      space = false;
    out += (space ? ' ' : '') + a;
  });
  return out;
}

function codeKeyOf(text: string): string | null {
  try {
    return codeText(kprogCodeAtoms(text));
  } catch {
    return null;
  }
}

function createCompleteState(model: ScratchModel): KProgState {
  const spec = model.kernel!;
  const prog = programOf(model);
  const lines = prog.code.replace(/\s+$/, '').split('\n');
  const blanks = lines.flatMap((l, i) => (BLANK_LINE_RE.test(l) ? [i] : []));
  if (blanks.length !== 1)
    throw new Error('Le programme doit avoir une seule ligne manquante, écrite « ____ ».');
  const blank = blanks[0]!;
  const checks = (prog.checks ?? []).map((text) => {
    const pl = readLine(kprogLineAtoms(text));
    if (pl.kind !== 'claims') throw new Error(`Test illisible : « ${text} »`);
    return { text, claims: pl.claims };
  });
  if (!checks.length && !prog.output?.length)
    throw new Error('Compléter un programme demande des tests (`checks` ou `output`).');
  const draws = drawsOf(model);
  const ks: KProgState = {
    mode: 'complete',
    spec,
    code: prog.code,
    call: null,
    trace: runProgram(''),
    points: [],
    last: 0,
    ends: [],
    watch: [],
    focusKey: null,
    cursor: 0,
    lines: [],
    condsDone: new Set(),
    callsDone: new Set(),
    evalsDone: new Set(),
    visited: [],
    loopTours: new Map(),
    draws,
    complete: {
      lines,
      blank,
      indent: BLANK_LINE_RE.exec(lines[blank]!)![1]!,
      checks,
      output: prog.output ?? null,
      cache: new Map(),
    },
    atLast: new Set(),
    concluded: false,
    answer: [],
    traps: [],
    path: spec.path ?? [],
    known: null,
    callCache: new Map(),
    funcs: new Set(),
    names: new Set(),
  };
  ks.traps = (model.traps ?? []).flatMap((t) => {
    const key = codeKeyOf(t.line);
    return key ? [{ key, title: t.title, why: t.why, line: kprogLine(key) }] : [];
  });
  ks.known = [
    spec.answer,
    ...ks.path,
    ...(model.traps ?? []).map((t) => t.line),
    ...(model.choices ?? []).flatMap((c) => (c.line ? [c.line] : [])),
  ].flatMap((t) => {
    try {
      return [kprogCodeAtoms(t)];
    } catch {
      return [];
    }
  });
  return ks;
}

/** l'appel d'un test écrit comme l'élève (« f(1,5) ») → en Python (« f(1.5) ») */
const pyCallOf = (atom: string): string => {
  const m = CALL_RE.exec(atom)!;
  return `${m[1]}(${m[2]!.replace(/−/g, '-').replace(/(\d),(\d)/g, '$1.$2')})`;
};

/** le programme complété par la ligne `text` passe-t-il les tests ? (mis en cache) */
function runComplete(ks: KProgState, text: string): CompleteResult {
  const cs = ks.complete!;
  const hit = cs.cache.get(text);
  if (hit) return hit;
  const lines = cs.lines.slice();
  lines[cs.blank] = cs.indent + text;
  const code = lines.join('\n') + '\n';
  const blankNo = cs.blank + 1;
  const fail = (title: string, message: string): CompleteResult => ({ ok: false, title, message });
  const crash = (e: { code: string; message: string; line: number }): CompleteResult =>
    e.code === 'syntax'
      ? fail(
          'Python ne comprend pas',
          e.line === blankNo
            ? `Python ne comprend pas ta ligne : ${e.message}`
            : `Avec ta ligne, le programme est mal écrit (ligne ${e.line}) : ${e.message}`,
        )
      : e.code === 'budget'
        ? fail(
            'Le programme ne s’arrête pas',
            'Avec ta ligne, le programme ne s’arrête pas (ou il est beaucoup trop long).',
          )
        : fail(
            'Erreur à l’exécution',
            `Avec ta ligne, le programme s’arrête sur une erreur (ligne ${e.line}) : ${e.message}`,
          );
  const result = ((): CompleteResult => {
    const opts = { trace: false, ...(ks.draws ? { draws: ks.draws } : {}) };
    const mod = runProgram(code, opts);
    if (mod.error) return crash(mod.error);
    if (cs.output) {
      const got = mod.output;
      if (got.length !== cs.output.length || got.some((l, i) => l !== cs.output![i]))
        return fail(
          'Ce n’est pas ce qu’il faut afficher',
          got.length
            ? `Avec ta ligne, le programme affiche « ${got.join(' ')} » au lieu de « ${cs.output.join(' ')} ».`
            : `Avec ta ligne, le programme n’affiche rien : il doit afficher « ${cs.output.join(' ')} ».`,
        );
    }
    for (const chk of cs.checks)
      for (const c of chk.claims) {
        let v: PyValue | null | undefined;
        if (c.call) {
          const run = runProgram(code, { ...opts, call: pyCallOf(c.target) });
          if (run.error) return crash(run.error);
          v = run.focus?.value ?? null;
        } else v = mod.globals[c.target];
        if (v === undefined || v === null)
          return fail(
            'Le test ne passe pas',
            `Avec ta ligne, ${c.target} n’existe pas à la fin du programme.`,
          );
        if (!valueMatches(v, c))
          return fail(
            'Le test ne passe pas',
            `Avec ta ligne, on obtient ${c.target} = ${formatValue(v)}, au lieu de ${valueText(c.value)}.`,
          );
      }
    return { ok: true };
  })();
  cs.cache.set(text, result);
  return result;
}

function completePasses(ks: KProgState, text: string): boolean {
  const key = codeKeyOf(text);
  return key !== null && runComplete(ks, key).ok;
}

const checksText = (cs: CompleteState): string =>
  [
    ...cs.checks.map((c) => c.text),
    ...(cs.output ? [`affiche « ${cs.output.join(' ')} »`] : []),
  ].join(' ; ');

function judgeComplete(ks: KProgState, A: string[], opts?: { verify?: boolean }): KProgVerdict {
  let text: string;
  try {
    text = codeText(A);
  } catch (e) {
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: e instanceof Error ? e.message : String(e),
      badChip: A.length - 1,
    };
  }
  const prefix = strictPrefixOfKnown(ks, A);
  const line = kprogLine(text);
  const base = { atoms: A, line };
  const key = text;
  const r = runComplete(ks, text);
  if (r.ok) {
    if (ks.concluded)
      return {
        kind: 'useless',
        ...base,
        title: 'Tu as déjà conclu',
        message: 'Le programme est complété et les tests passent.',
        pmove: { key },
      };
    const v: KProgVerdict = {
      kind: 'step',
      ...base,
      final: true,
      hora: 3,
      rule: {
        id: PYTHON_STEP_IDS.complete,
        card: CARD,
        label: 'Compléter le programme',
        hora: 3,
        why: `Avec cette ligne, le programme passe les tests : ${checksText(ks.complete!)}.`,
      },
      pmove: { key, conclude: true },
    };
    return prefix && !opts?.verify ? { ...v, kind: 'ready' } : v;
  }
  if (prefix && !opts?.verify) return { kind: 'building', atoms: A, complete: true };
  const trap = ks.traps.find((t) => t.key === key);
  if (trap)
    return {
      kind: 'trap',
      ...base,
      title: 'Piège : ' + trap.title.charAt(0).toLowerCase() + trap.title.slice(1),
      message: trap.why,
      trap: { line: trap.line, title: trap.title, why: trap.why },
      badChip: -1,
    };
  if (!opts?.verify)
    return {
      kind: r.title === 'Python ne comprend pas' ? 'open' : 'pending',
      atoms: A,
      complete: r.title !== 'Python ne comprend pas',
    };
  return { kind: 'false', ...base, title: r.title, message: r.message };
}

/** Les bouts de code proposés : mots-clés et noms de la ligne, nombres, opérations. */
export function kprogCodePaletteParts(model: ScratchModel): {
  words: string[];
  names: string[];
  numbers: string[];
  ops: string[];
} {
  const spec = model.kernel!;
  const code = spec.program?.code ?? '';
  const texts = [
    spec.answer,
    ...(spec.path ?? []),
    ...(model.traps ?? []).map((t) => t.line),
    ...(model.choices ?? []).flatMap((c) => (c.line ? [c.line] : [])),
  ];
  const atoms = texts.flatMap((t) => {
    try {
      return kprogCodeAtoms(t);
    } catch {
      return [];
    }
  });
  const codeNames = (() => {
    try {
      return kprogCodeAtoms(code.replace(/_{3,}/g, ' '));
    } catch {
      return [];
    }
  })().filter((a) => /^[A-Za-z_À-ɏ]/.test(a));
  const uniq = (xs: string[]) => [...new Set(xs)];
  const isName = (a: string) => /^[A-Za-z_À-ɏ][A-Za-z0-9_À-ɏ]*$/.test(a);
  const words = uniq(
    atoms.filter((a) => isName(a) && (PY_KEYWORDS.has(a) || BUILTIN_WORDS.has(a))),
  );
  const names = uniq(
    [...codeNames, ...atoms].filter(
      (a) => isName(a) && !PY_KEYWORDS.has(a) && !BUILTIN_WORDS.has(a) && !words.includes(a),
    ),
  );
  const numbers = uniq(atoms.filter((a) => /^\d/.test(a))).sort((a, b) => Number(a) - Number(b));
  const ops = uniq(atoms.filter((a) => !isName(a) && !/^\d/.test(a) && !/^["']/.test(a)));
  return { words, names, numbers, ops };
}
/** fonctions et constantes de Python proposées comme des mots (« range », « print ») */
const BUILTIN_WORDS = new Set([
  'range',
  'print',
  'len',
  'abs',
  'min',
  'max',
  'sum',
  'int',
  'float',
  'round',
  'True',
  'False',
  'None',
  'randint',
  'random',
  'append',
]);

/* ------------------------------ version du modèle ------------------------------ */

const plusCache = new WeakMap<object, boolean>();

/**
 * La feuille Python demande-t-elle le moteur v29 ? (objectif `complete`, tirages, tests,
 * affichage d'un algorithme, `random`, un calcul « n % 2 = 1 », `None`, ou une boucle de
 * PYTHON_JUMP_MIN_TOURS tours ou plus, qu'on trace en sautant des tours)
 */
export function usesKernelPythonPlus(model: Record<string, unknown>): boolean {
  const cached = plusCache.get(model);
  if (cached !== undefined) return cached;
  const out = ((): boolean => {
    try {
      const k = model.kernel as KernelSpec | undefined;
      const p = k?.program;
      if (!k || !p) return false;
      if (k.goal?.kind === 'complete' || p.draws || p.checks || p.output || p.display) return true;
      if (/\b(import\s+random|from\s+random)\b/.test(p.code)) return true;
      // un test « … or … » / « … and … » : l'opérande qui décide s'écrit (v29)
      if (/^\s*(if|elif|while)\b.*\b(and|or)\b/m.test(p.code)) return true;
      const m = model as {
        traps?: { line?: string }[];
        choices?: { line?: string | null }[];
      };
      const texts = [
        k.answer,
        ...(k.path ?? []),
        ...(m.traps ?? []).map((t) => t.line ?? ''),
        ...(m.choices ?? []).map((c) => c.line ?? ''),
      ];
      for (const t of texts) {
        if (/\bNone\b/.test(t)) return true;
        // les appels « g(−4) » ôtés : un calcul avant le premier « = », ou un « % »
        const bare = t.replace(/[A-Za-z_À-ɏ][\wÀ-ɏ]*\s*\([^()]*\)/g, 'F');
        if (bare.includes('%')) return true;
        if (!bare.includes(';')) {
          const eq = bare.search(/[=≈]/);
          if (eq > 0 && /[+−×÷*/-]/.test(bare.slice(0, eq))) return true;
        }
      }
      const t = runProgram(p.code, p.call ? { call: p.call } : {});
      if (!t.ok) return false;
      const tours = new Map<number, number>();
      t.points.forEach((pt) => {
        if (pt.unit === 'iteration' && pt.loop) tours.set(pt.loop, (tours.get(pt.loop) ?? 0) + 1);
      });
      return [...tours.values()].some((n) => n >= PYTHON_JUMP_MIN_TOURS);
    } catch {
      return false;
    }
  })();
  plusCache.set(model, out);
  return out;
}
